// @ts-strict-ignore
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CustomArticleInfoVO } from '@project/components/article/model/vo/custom-article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Dictionary } from 'typescript-collections';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { Vector3 } from 'three';
import { IOperation } from '@shared/undo/operation/i-operation';
import { BaseAdvancedOperation } from '@shared/undo/operation/base-advanced-operation';
import { UndoInsertArticlesByLocationOperation } from '@shared/undo/operation/undo-insert-articles-by-location-operation';
import { UndoReplaceArticlesByLocationOperation } from '@shared/undo/operation/undo-replace-articles-by-location-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { CloneUtils } from '@shared/util/clone-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleService } from '../service/insert-article.service';
import { Log } from '@shared/debug/log';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { StringUtils } from '@shared/util/string-utils';

export class CustomArticleManager {
  private static readonly INSERT_CUSTOM_ARTICLE: string = 'insertCustomArticle';
  private static readonly LANGUAGE_KEY_NOT_ENOUGH_FREE_SPACE_MESSAGE: string = 'customArticleManager.error.message.not_enough_free_space';
  private static readonly LANGUAGE_KEY_NOT_ENOUGH_FREE_SPACE_TITLE: string = 'customArticleManager.error.title.not_enough_free_space';
  private static readonly LANGUAGE_KEY_INSERT_FAILURE_TITLE: string = 'customArticleManager.error.title.insert_failure';

  private _mountingRailUsage: ArticleUsageVO;
  private _insertHelper: InsertArticleHelper;
  private _articleUsageToCreate: ArticleUsageVO;
  private _detachOperation: UndoDetachArticlesOperation;

  constructor(
    private insertArticleUtils: InsertArticleUtils,
    private alertHelper: AlertHelper,
    private mountingRailHelper: MountingRailHelper,
    private projectArticleService: ProjectArticleService,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private containerService: ContainerService,
    private undoRedoUtils: UndoRedoUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private _insertArticleService: InsertArticleService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    this.init();
  }

  private init(): void {
    this._mountingRailUsage = this.mountingRailHelper.getMountingRailUsage();
    if (this._mountingRailUsage) {
      this._insertHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(
        this.containerService.findArticleContainerInGlobal(this._mountingRailUsage.id)
      );
    }
  }

  public async recreateCustomArticle(articleInfo: CustomArticleInfoVO, customArticleUsage: ArticleUsageVO): Promise<void> {
    if (customArticleUsage && this._insertHelper) {
      const customContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(customArticleUsage.id);
      const customArticleLine: AdvancedLine = this._insertHelper.getArticleLine(customContainer);
      await this.insertCustomArticle(
        customContainer.article,
        articleInfo,
        this._insertHelper.getDistanceFromStart(customArticleLine.start),
        customArticleUsage
      );
    } else {
      this.clear();
    }
  }

  public async insertCustomArticle(
    article: ArticleVO,
    articleInfo: CustomArticleInfoVO, offset: number,
    articleUsage: ArticleUsageVO = null
  ): Promise<void> {
    if (this.isConfiguredCustomArticleValid(articleInfo, offset, articleUsage ? articleUsage.id : null)) {
      this._articleUsageToCreate = articleUsage ? CloneUtils.cloneArticleUsageVO(articleUsage) : null;
      if (!this._articleUsageToCreate) {
        this._articleUsageToCreate = this.projectArticleService.createUnstoredNewArticleUsage(article.identity, article.version);
      } else {
        ReferenceArticleUsageUtils.removeAllReferences(this._articleUsageToCreate);
      }
      this._articleUsageToCreate.metaElementUsages = [];
      ArticleUsageUtils.replaceArticleUsageId([this._articleUsageToCreate], this._articleUsageToCreate.id, StringUtils.createUuid());
      CustomArticleUtils.applyCustomArticleInfoToArticleUsage(this._articleUsageToCreate, articleInfo);
      this.projectArticleService.storeArticleUsage(this._articleUsageToCreate);

      if (articleUsage) {
        this.selectionModelService.removeAllSelections();
        await this.detachArticleUsage(articleUsage);
      }

      const articleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
      articleLocation.articleOffset = offset;
      articleLocation.checkConnectionProperties = false;
      articleLocation.rangeOnLineArticleUsage = this._mountingRailUsage;
      articleLocation.existingArticleUsageId = this._articleUsageToCreate.id;

      try {
        const insertedData = await this._insertArticleService.insertArticleByArticleLocation(article, articleLocation, CustomArticleManager.INSERT_CUSTOM_ARTICLE);
        await this.customArticleInserted(insertedData);
      } catch (error) {
        Log.error('Could not inser article: ', error);
        await this.customArticleInserted_failure();
      }
    } else {
      this.clear();
    }
  }

  private isConfiguredCustomArticleValid(customArticleConfiguration: CustomArticleInfoVO, position: number, customUsageId: string): boolean {
    const isValid: boolean = this._insertHelper.isInsertOperationAvailable(
      position,
      customArticleConfiguration.articleWidth,
      customUsageId == null ? null : [customUsageId]
    );
    if (!isValid) {
      this.displayNotEnoughFreeSpaceMessage();
    }
    return isValid;
  }

  private async detachArticleUsage(articleUsage: ArticleUsageVO): Promise<void> {
    const usageIdsToDetach: string[] = ReferenceArticleUsageUtils.getForwardReferenceArticleUsageIds(articleUsage);
    usageIdsToDetach.push(articleUsage.id);
    this._detachOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(usageIdsToDetach);
    const detachedArticle = await this.detachArticleUsageService.detachArticleUsageFromProjectById(articleUsage.id);
    if (detachedArticle) {
      this.applicationRefreshService.notifyProjectChange();
    }
  }

  private displayNotEnoughFreeSpaceMessage(): void {
    this.displayErrorMessage(
      CustomArticleManager.LANGUAGE_KEY_NOT_ENOUGH_FREE_SPACE_MESSAGE,
      CustomArticleManager.LANGUAGE_KEY_NOT_ENOUGH_FREE_SPACE_TITLE
    );
  }

  private displayErrorMessage(messageLocaleKey: string, titleLocaleKey: string): void {
    this.alertHelper.showError(titleLocaleKey, messageLocaleKey);
  }

  private async pushInsertCustomArticleOperation(insertedGUIDs: string[], moveMap: Dictionary<string, Vector3>, additionalUndoOperations: IOperation[]): Promise<void> {
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(
      insertedGUIDs
    );
    if (!attachOperation) {
      return;
    }
    let undoOperation: BaseAdvancedOperation = null;
    if (!this._detachOperation) {
      undoOperation = new UndoInsertArticlesByLocationOperation(attachOperation, moveMap, this.insertArticleUtils);
    } else {
      undoOperation = new UndoReplaceArticlesByLocationOperation(attachOperation, this._detachOperation, moveMap, this.insertArticleUtils);
    }
    undoOperation.additionalOperations = additionalUndoOperations;
    await this.undoRedoHelper.pushUndoOperation(undoOperation);
  }

  private async revertRemovedCustomArticle(): Promise<void> {
    if (!this._detachOperation) {
      return;
    }
    await this._detachOperation.performUndo();
  }

  private async customArticleInserted(data: InsertedArticleData): Promise<void> {
    const baseGuid: string = data.baseArticleGUID;
    if (!this._articleUsageToCreate || baseGuid !== this._articleUsageToCreate.id) {
      return;
    }
    await this.pushInsertCustomArticleOperation(data.insertedArticleGUIDs, data.moveArticlesMap, data.undoOperations);
  }

  private async customArticleInserted_failure(message: any = {}): Promise<void> {
    this.alertHelper.showError(CustomArticleManager.LANGUAGE_KEY_INSERT_FAILURE_TITLE, message);
    await this.revertRemovedCustomArticle();
  }

  private clear(): void {
    this._mountingRailUsage = null;
    this._detachOperation = null;
    this._insertHelper = null;
    this._articleUsageToCreate = null;
  }
}
